import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { ChatInterface } from '@/components/chat/ChatInterface'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { AccountMenu } from '@/components/tenant/AccountMenu'
import { TenantBooking } from '@/components/tenant/TenantBooking'
import { ClientAppointmentsDialog } from '@/components/tenant/ClientAppointments'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { useRole } from '@/hooks/useRole'
import { useBranding } from '@/contexts/BrandingContext'
import {
  APPOINTMENTS_CHANGED_EVENT,
  MANAGE_APPOINTMENTS_EVENT,
  manageRequestBookingId,
} from '@/lib/widgetEvents'

export default function TenantChat() {
  const navigate = useNavigate()
  // One dialog, two ways in: the account menu, and the appointments card inside
  // the booking chat (which names the appointment tapped, when it was one).
  const [appointmentsOpen, setAppointmentsOpen] = useState(false)
  const [focusBookingId, setFocusBookingId] = useState<string | null>(null)
  const { tenant } = useParams<{ tenant: string }>()
  const { signOut, authState } = useAuth()
  const { tenantConfig, userRole, setupRequired, status, noRecord } = useRole()
  const branding = useBranding()

  const slug = tenant ?? ''
  const isClient = authState.status === 'authenticated' && userRole === 'customer'

  // Claims the widget's manage request. preventDefault() is the reply: an
  // unclaimed request makes the widget ask its assistant instead, which is right
  // for an embed with no dialog and wrong here.
  useEffect(() => {
    if (!isClient) return
    const onManage = (event: Event) => {
      event.preventDefault()
      setFocusBookingId(manageRequestBookingId(event))
      setAppointmentsOpen(true)
    }
    window.addEventListener(MANAGE_APPOINTMENTS_EVENT, onManage)
    return () => window.removeEventListener(MANAGE_APPOINTMENTS_EVENT, onManage)
  }, [isClient])

  // The slug entry has two front doors and they show the SAME booking view.
  // Signed out, the visitor books as a guest; signed in as a client, they book
  // through the identical guided flow with the widget in signed-in mode — so
  // the only difference between the doors is that the AI knows who they are and
  // every action it takes is against their record (v1-widget resolves the
  // customer from the JWT). Staff and owners are a different audience entirely
  // and keep the full role-aware chat below.
  if (authState.status === 'unauthenticated') {
    return (
      <TenantBooking
        slug={slug}
        intro="Book as a guest below — no account needed. Already a customer? Sign in to view and manage your appointments."
        headerAction={(
          <Link
            to={`/login?tenant=${encodeURIComponent(slug)}`}
            className="shrink-0 rounded-md px-3.5 py-1.5 text-sm font-medium text-white"
            style={{ backgroundColor: branding.primaryColor }}
          >
            Sign in
          </Link>
        )}
      />
    )
  }

  // Signed in with an email the business has no client record under: they can
  // still book as a guest, and are told which email to use. Never the operator chat.
  if (status === 'no_record') {
    const signedInAs = noRecord?.email ?? (authState.status === 'authenticated' ? authState.user.email ?? '' : '')
    return (
      <TenantBooking
        slug={slug}
        intro={`You’re signed in as ${signedInAs}, but we can’t find a client record under that email. Book as a guest below, or sign out and sign in with the email you gave us when you booked.`}
        headerAction={(
          <AccountMenu label={signedInAs} color={branding.primaryColor} onSignOut={signOut} />
        )}
      />
    )
  }

  const businessName = tenantConfig?.name ?? 'Porter'
  const ownerName = tenantConfig?.owner_name ?? ''
  const userEmail = authState.status === 'authenticated' ? authState.user.email ?? '' : ''
  const displayName = ownerName || userEmail
  const isOwnerOrStaff = userRole === 'owner' || userRole === 'staff'

  if (userRole === 'customer') {
    return (
      <>
        <TenantBooking
          slug={slug}
          // Read from the live session on every render, never snapshotted: the
          // SDK rotates this token roughly hourly and v1-widget hard-401s a stale
          // one, which would strand a client mid-booking.
          authToken={authState.status === 'authenticated' ? authState.session.access_token : null}
          intro="You're signed in — your booking goes straight onto your account, so there's no need to enter your details."
          // The bell rides beside the account menu rather than inside it: a
          // client's own appointment confirmations and changes land here, and the
          // same component serves both audiences so the two can never drift.
          // It is deliberately absent from the GUEST branch above — there is no
          // session, so there is nobody to have notifications.
          //
          // TenantBooking gets the same props as the guest door plus the token;
          // the appointments card lives INSIDE the widget's chat, so nothing new
          // is added to the booking column here.
          headerAction={(
            <div className="flex shrink-0 items-center gap-1">
              <NotificationBell />
              <AccountMenu
                label={userEmail}
                color={branding.primaryColor}
                onSignOut={signOut}
              >
                <DropdownMenuItem
                  className="rounded-none px-3.5 py-3"
                  onSelect={() => {
                    setFocusBookingId(null)
                    setAppointmentsOpen(true)
                  }}
                >
                  <span className="text-sm font-medium text-foreground">My appointments</span>
                </DropdownMenuItem>
              </AccountMenu>
            </div>
          )}
        />
        <ClientAppointmentsDialog
          slug={slug}
          brandColor={branding.primaryColor}
          open={appointmentsOpen}
          onOpenChange={setAppointmentsOpen}
          focusBookingId={focusBookingId}
          // The chat card above shows the same diary; without this it keeps
          // offering an appointment the client has just cancelled.
          onChanged={() => window.dispatchEvent(new Event(APPOINTMENTS_CHANGED_EVENT))}
        />
      </>
    )
  }

  const title = isOwnerOrStaff ? `${businessName} - ${displayName}` : businessName
  const placeholder = isOwnerOrStaff
    ? 'Ask or tell me anything…'
    : 'Book, check or cancel an appointment…'

  // Trigger and panel travel together as one node because the chat header owns
  // where the avatar lands (headerLeft) — the menu anchors to it from there.
  const userButton = (
    <AccountMenu
      label={displayName}
      sublabel={userRole}
      color={branding.primaryColor}
      onSignOut={signOut}
      align="start"
    >
      {setupRequired && (
        <DropdownMenuItem
          className="rounded-none px-3.5 py-3"
          // Pre-migration this only closed the menu — the item was a dead end,
          // so an owner with an incomplete setup had no way through to it.
          onSelect={() => navigate('/setup')}
        >
          <span className="text-sm font-medium" style={{ color: branding.primaryColor }}>
            Complete setup
          </span>
        </DropdownMenuItem>
      )}
    </AccountMenu>
  )

  return (
    <div className="flex h-full min-h-0 flex-col bg-muted">
      <div className="relative flex min-h-0 flex-1 flex-col">
        <ChatInterface
          title={title}
          placeholder={placeholder}
          headerLeft={userButton}
          hideAiAvatar
          showTodayCalendar={isOwnerOrStaff}
          timezone={tenantConfig?.timezone}
        />
      </div>
    </div>
  )
}
